import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Rating,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Business,
  LocationOn,
  Settings,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const InvEditSupplier = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchSupplier();
  }, [id]);

  const fetchSupplier = async () => {
    try {
      const response = await suppliersAPI.getById(id);
      setSupplier(response.data.data.supplier);
      setFormData(response.data.data.supplier);
    } catch (error) {
      enqueueSnackbar('Error fetching supplier', { variant: 'error' });
      navigate('/suppliers');
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await suppliersAPI.update(id, formData);
      enqueueSnackbar('Supplier updated successfully!', { variant: 'success' });
      navigate('/hospital/hospital-inventory/suppliers');
    } catch (error) {
      enqueueSnackbar('Error updating supplier', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!supplier) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
            variant="outlined"
            sx={{ 
              color: 'white', 
              borderColor: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: 'white'
              }
            }}
          >
            Back to Suppliers
          </Button>
        </Box>
        <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Edit Supplier
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mt: 1, fontSize: '1.2rem' }}>
          Update supplier information and details
        </Typography>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} lg={6}>
            {/* Basic Information Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Business sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                    Basic Information
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Supplier Name"
                      value={formData.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={formData.contactPerson || ''}
                      onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                      required
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email Address"
                      value={formData.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      required
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Alternate Phone"
                      value={formData.alternatePhone || ''}
                      onChange={(e) => handleInputChange('alternatePhone', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Business Information Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, mt: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Business Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="GST Number"
                      value={formData.gstNumber || ''}
                      onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PAN Number"
                      value={formData.panNumber || ''}
                      onChange={(e) => handleInputChange('panNumber', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} lg={6}>
            {/* Address Information Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <LocationOn sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                    Address Information
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.address?.street || ''}
                      onChange={(e) => handleInputChange('address.street', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.address?.city || ''}
                      onChange={(e) => handleInputChange('address.city', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.address?.state || ''}
                      onChange={(e) => handleInputChange('address.state', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.address?.zipCode || ''}
                      onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.address?.country || 'India'}
                      onChange={(e) => handleInputChange('address.country', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Supplier Settings Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, mt: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Settings sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                    Supplier Settings
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Payment Terms"
                      value={formData.paymentTerms || '30_days'}
                      onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    >
                      <MenuItem value="advance" sx={{ fontSize: '1.1rem' }}>Advance</MenuItem>
                      <MenuItem value="7_days" sx={{ fontSize: '1.1rem' }}>7 Days</MenuItem>
                      <MenuItem value="15_days" sx={{ fontSize: '1.1rem' }}>15 Days</MenuItem>
                      <MenuItem value="30_days" sx={{ fontSize: '1.1rem' }}>30 Days</MenuItem>
                      <MenuItem value="45_days" sx={{ fontSize: '1.1rem' }}>45 Days</MenuItem>
                      <MenuItem value="60_days" sx={{ fontSize: '1.1rem' }}>60 Days</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Lead Time (Days)"
                      value={formData.leadTime || 7}
                      onChange={(e) => handleInputChange('leadTime', parseInt(e.target.value))}
                      inputProps={{ min: 1 }}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography component="legend" sx={{ fontSize: '1.1rem', mb: 1, fontWeight: 'medium' }}>
                      Supplier Rating
                    </Typography>
                    <Rating
                      value={formData.rating || 3}
                      onChange={(event, newValue) => {
                        handleInputChange('rating', newValue);
                      }}
                      size="large"
                      sx={{ fontSize: '2.5rem' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isActive !== undefined ? formData.isActive : true}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: '1.5rem' } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                          Active Supplier
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Additional Notes"
                      value={formData.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      size="medium"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mt: 4, 
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
              variant="outlined"
              size="large"
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1,
                minWidth: 160
              }}
            >
              {loading ? 'Updating...' : 'Update Supplier'}
            </Button>
          </Box>
        </Paper>
      </form>
    </Container>
    </Box>
  );
};